import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import { Tabs } from "@package/subframe/components/Tabs";
import {
  FeatherBell,
  FeatherCheckCircle,
  FeatherDownload,
  FeatherHistory,
  FeatherList,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useMemo, useState } from "react";
import { toast } from "sonner";

import { MonitoringTable } from "@/components/monitoring/monitoring-table";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TableScrollContainer } from "@/components/ui/table-scroll-container";
import { useNursery } from "@/contexts/NurseryContext";
import { useAcknowledgeCheckFailureAlertCommandControllerAcknowledge } from "@/generated/check-failure-alerts/check-failure-alerts";
import { useListColdStorageQueryControllerList } from "@/generated/cold-storage/cold-storage";
import { useListLogTypesQueryControllerList } from "@/generated/log-types/log-types";
import { EmergencyLightCheckKind } from "@/generated/model/emergencyLightCheckKind";
import type { MonitoringEntryDto } from "@/generated/model/monitoringEntryDto";
import { useListRoomsQueryControllerList } from "@/generated/rooms/rooms";
import { useMonitoringData } from "@/hooks/use-monitoring-data";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";
import {
  EMERGENCY_LIGHT_FLICK_TEST_SLUG,
  EMERGENCY_LIGHT_VISUAL_CHECK_SLUG,
  EMERGENCY_LIGHTING_LOG_TYPE_SLUG,
  FIRE_DRILL_SLUG,
  FIRE_EXTINGUISHER_CHECK_SLUG,
  type MonitoringHistoryView,
  monitoringScopeFor,
  type MonitoringSortColumn,
  type MonitoringTab,
  type MonitoringView,
  monitoringViewFor,
  monitoringViewTarget,
  PAGE_SIZE,
  type SortDir,
} from "@/lib/monitoring";
import {
  ALERT_ACK_ERROR,
  ALERT_ACKNOWLEDGED,
} from "@/lib/monitoring/acknowledge-copy";
import { invalidateMonitoringQueries } from "@/lib/monitoring/invalidate-monitoring-queries";
import {
  downloadMonitoringCsv,
  exportErrorMessage,
} from "@/lib/monitoring-export";

const routeApi = getRouteApi("/_authenticated/monitoring/");

/**
 * The monitored records that aren't LogTypes and so have no id: picking one
 * sends `checkSlug` instead of `logTypeId`. Sentinel values are prefixed by
 * kind — the extinguisher check is its own bounded context, the fire drill an
 * event type. Kept as one list so the option, the selected value and the filter
 * it sends can't drift apart.
 */
const NON_LOG_TYPE_OPTIONS = [
  {
    value: "CHECK:fire-extinguisher-check",
    label: "Fire Extinguisher Visual Check",
    slug: FIRE_EXTINGUISHER_CHECK_SLUG,
  },
  {
    value: "CHECK:emergency-light-flick-test",
    label: "Emergency Lighting: Monthly Flick Test",
    slug: EMERGENCY_LIGHT_FLICK_TEST_SLUG,
  },
  {
    value: "CHECK:emergency-light-visual-check",
    label: "Emergency Lighting: Visual Check",
    slug: EMERGENCY_LIGHT_VISUAL_CHECK_SLUG,
  },
  // Deactivated, so list-log-types no longer returns it — without this its
  // room-level history could not be filtered to.
  {
    value: "CHECK:emergency-lighting",
    label: "Emergency Lighting: Room-level (old format)",
    slug: EMERGENCY_LIGHTING_LOG_TYPE_SLUG,
  },
  {
    value: "EVENT:fire-drill",
    label: "Fire Drill / Evacuation",
    slug: FIRE_DRILL_SLUG,
  },
] as const;

/**
 * Default sort when the user hasn't picked a column: newest first, by the date
 * each list is actually about — resolved alerts read as "what did we close
 * most recently", everything else as "what happened most recently".
 */
const DEFAULT_SORT: { column: MonitoringSortColumn; dir: SortDir } = {
  column: "triggered",
  dir: "desc",
};
const RESOLVED_DEFAULT_SORT: { column: MonitoringSortColumn; dir: SortDir } = {
  column: "resolved",
  dir: "desc",
};

/**
 * Segmented control for the History sub-views.
 *
 * Hand-rolled on native buttons rather than Subframe's ToggleGroup: that
 * component renders its items as divs through Radix `asChild`, which leaves
 * every item at tabindex -1 and binds no key handler — the control ends up
 * mouse-only. Buttons give focus, Enter and Space for free. The classes mirror
 * the Subframe toggle so it still reads as the design system's control.
 */
function HistoryViewToggle({
  value,
  onChange,
}: {
  value: MonitoringHistoryView;
  onChange: (next: MonitoringHistoryView) => void;
}) {
  const options: {
    value: MonitoringHistoryView;
    label: string;
    icon: ReactNode;
  }[] = [
    { value: "readings", label: "Readings", icon: <FeatherList /> },
    {
      value: "resolved-alerts",
      label: "Resolved alerts",
      icon: <FeatherCheckCircle />,
    },
  ];

  return (
    <div
      role="group"
      aria-label="History view"
      className="flex items-center gap-0.5 overflow-hidden rounded-md bg-neutral-100 px-0.5 py-0.5"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className="group flex h-7 cursor-pointer items-center justify-center gap-2 rounded-md px-2 py-1 aria-pressed:bg-default-background aria-pressed:shadow-sm"
        >
          <span className="text-body font-body text-subtext-color group-hover:text-default-font group-aria-pressed:text-default-font">
            {option.icon}
          </span>
          <span className="whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color group-hover:text-default-font group-aria-pressed:text-default-font">
            {option.label}
          </span>
        </button>
      ))}
    </div>
  );
}

// Both History lists are windowed to the current month server-side, and the
// page has no range control yet — so the copy names the period rather than
// leaving "this period" to stand for something the user can't see or change.
const EMPTY_STATE: Record<MonitoringView, string> = {
  alerts: "No open alerts.",
  readings: "No monitoring entries to show this month.",
  "resolved-alerts": "No alerts resolved this month.",
};

/** Pagination's "showing 1 to 2 of N …" noun, per list. */
const ITEM_NOUN: Record<MonitoringView, string> = {
  alerts: "alerts",
  readings: "entries",
  "resolved-alerts": "resolved alerts",
};

function AlertCountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      data-testid="alerts-count-badge"
      className="ml-2 inline-flex items-center justify-center rounded-full bg-error-50 px-2 text-caption-bold font-caption-bold text-error-600"
    >
      {count}
    </span>
  );
}

export function MonitoringPage() {
  const navigate = useNavigate();
  const search = routeApi.useSearch();
  const queryClient = useQueryClient();
  const { selectedNurseryId } = useNursery();

  const tab: MonitoringTab = search.tab ?? "alerts";
  const historyView: MonitoringHistoryView = search.view ?? "readings";
  const view = monitoringViewFor(tab, historyView);
  const [logTypeId, setLogTypeId] = useState<string | undefined>(undefined);
  const [checkSlug, setCheckSlug] = useState<string | undefined>(undefined);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sortColumn, setSortColumn] = useState<
    MonitoringSortColumn | undefined
  >(undefined);
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // The tab and sub-view live in the URL, so they can change without going
  // through the click handlers (browser back/forward, direct links). Reset
  // paging and sort during render whenever either does: a stale offset from
  // the other list renders a misleading empty state, and a stale `resolved`
  // sort carried onto a list whose rows have no resolution date silently
  // collapses the ordering onto the tie-breakers.
  const [lastView, setLastView] = useState(view);
  if (view !== lastView) {
    setLastView(view);
    setPage(0);
    setSortColumn(undefined);
    setSortDir("desc");
  }

  const { data: logTypesResponse } = useListLogTypesQueryControllerList(
    selectedNurseryId ?? "",
    { query: { enabled: !!selectedNurseryId } },
  );
  // Memoised so the `?? []` fallback doesn't hand a fresh array to the options
  // memo below on every render.
  const logTypes = useMemo(
    () => logTypesResponse?.data ?? [],
    [logTypesResponse?.data],
  );

  const { data: coldStorageResponse } = useListColdStorageQueryControllerList(
    selectedNurseryId ?? "",
    { includeInactive: false },
    { query: { enabled: !!selectedNurseryId } },
  );
  const { data: roomsResponse } = useListRoomsQueryControllerList(
    selectedNurseryId ?? "",
    undefined,
    { query: { enabled: !!selectedNurseryId } },
  );
  const locations = [
    ...(coldStorageResponse?.data ?? []),
    ...(roomsResponse?.data ?? []),
  ];

  // Resolve effective sort: explicit column, else the default — so the table
  // header always reflects the order the rows are actually in.
  const defaultSort =
    view === "resolved-alerts" ? RESOLVED_DEFAULT_SORT : DEFAULT_SORT;
  const effectiveSort = sortColumn
    ? { column: sortColumn, dir: sortDir }
    : defaultSort;

  const { entries, total, activeAlertCount, isLoading, isPlaceholder } =
    useMonitoringData({
      nurseryId: selectedNurseryId ?? undefined,
      scope: monitoringScopeFor(tab, historyView),
      logTypeId,
      checkSlug,
      locationId,
      sortBy: effectiveSort.column,
      sortDir: effectiveSort.dir,
      page,
      pageSize,
    });

  const acknowledgeMutation =
    useAcknowledgeCheckFailureAlertCommandControllerAcknowledge();
  const [acknowledgingAlertId, setAcknowledgingAlertId] = useState<
    string | null
  >(null);

  // Acknowledging clears the alert from this list, decrements the tab badge and
  // returns the dashboard Monitoring row to green — so both the monitoring
  // queries and the dashboard status have to be invalidated.
  const handleAcknowledge = async (alertId: string) => {
    setAcknowledgingAlertId(alertId);
    try {
      await acknowledgeMutation.mutateAsync({ alertId });
      toast.success(ALERT_ACKNOWLEDGED);
    } catch (error) {
      // A 409 says why: the check hasn't passed since it failed (NAU-411 AC15).
      toast.error(extractApiErrorMessage(error, ALERT_ACK_ERROR));
    } finally {
      // Every outcome, not just success: this list is only fetched on arrival, so
      // skipping the refresh on failure leaves a row the server has closed.
      await invalidateMonitoringQueries(queryClient);
      setAcknowledgingAlertId(null);
    }
  };

  // The dropdown mixes LogType-backed monitors with records that have no
  // LogType, so options are merged into one list and sorted by label —
  // otherwise the appended non-LogType entries always trail the rest.
  const monitoringTypeOptions = useMemo(
    () =>
      [
        ...logTypes.map((logType) => ({
          value: logType.id,
          label: logType.name,
        })),
        ...NON_LOG_TYPE_OPTIONS.map(({ value, label }) => ({ value, label })),
      ].sort((a, b) => a.label.localeCompare(b.label)),
    [logTypes],
  );

  // The dropdown mixes LogType ids with slug-only records, so the selected value
  // is one or the other and only one filter is ever sent.
  const selectedCheckTypeValue =
    NON_LOG_TYPE_OPTIONS.find((option) => option.slug === checkSlug)?.value ??
    logTypeId ??
    "ALL";

  const handleCheckTypeChange = (value: string) => {
    const nonLogType = NON_LOG_TYPE_OPTIONS.find(
      (option) => option.value === value,
    );
    if (nonLogType) {
      setLogTypeId(undefined);
      setCheckSlug(nonLogType.slug);
    } else {
      setCheckSlug(undefined);
      setLogTypeId(value === "ALL" ? undefined : value);
    }
    setPage(0);
  };

  // Omitting `view` drops the sub-view from the URL, so returning to Alerts
  // always lands on a clean History next time. Guarded on the current tab
  // because that reset shouldn't fire when the user clicks the tab they are
  // already on — that would throw them out of the resolved sub-view.
  const setTab = (next: MonitoringTab) => {
    if (next === tab) return;
    navigate({ to: "/monitoring", search: { tab: next } });
  };

  const setHistoryView = (next: MonitoringHistoryView) => {
    navigate({ to: "/monitoring", search: { tab: "history", view: next } });
  };

  const handleSort = (column: MonitoringSortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDir("asc");
    }
    setPage(0);
  };

  // `monitoringViewTarget` resolves the destination for both the View button
  // and the row click, so the affordance and the navigation can never disagree.
  const canView = (entry: MonitoringEntryDto) =>
    monitoringViewTarget(entry, logTypes) !== null;

  const [isExporting, setIsExporting] = useState(false);

  // AC24. Exports what the filters currently describe, not the visible page —
  // the whole filtered set. The server caps it and refuses rather than
  // truncating, so a 400 here means "narrow the range", not "export failed".
  const handleExport = async () => {
    if (!selectedNurseryId || isExporting) return;
    setIsExporting(true);
    try {
      await downloadMonitoringCsv(selectedNurseryId, {
        scope: monitoringScopeFor(tab, historyView),
        logTypeId,
        checkSlug,
        locationId,
        sortBy: effectiveSort.column,
        sortDir: effectiveSort.dir,
      });
    } catch (error) {
      toast.error(
        (await exportErrorMessage(error)) ??
          "Failed to export — please try again",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleView = (entry: MonitoringEntryDto) => {
    const target = monitoringViewTarget(entry, logTypes);
    if (!target) return;

    switch (target.kind) {
      case "incident":
        navigate({
          to: "/incidents/$incidentId",
          params: { incidentId: target.incidentId },
        });
        return;
      case "hot-holding-alert":
        navigate({
          to: "/hot-holding-alerts/$alertId",
          params: { alertId: target.alertId },
        });
        return;
      case "extinguisher-check":
        navigate({ to: `/fire-extinguisher-checks/${target.checkId}` });
        return;
      case "emergency-light-check":
        navigate({ to: `/emergency-light-checks/${target.checkId}` });
        return;
      case "event-record":
        navigate({
          to: "/event-records/$eventRecordId",
          params: { eventRecordId: target.eventRecordId },
        });
        return;
      case "legacy-emergency-lighting":
        navigate({
          to: "/emergency-light-checks",
          search: { kind: EmergencyLightCheckKind.FLICK_TEST },
        });
        return;
      case "log-day":
        navigate({
          to: `/logs/${target.logTypeId}`,
          search: target.coldStorageId
            ? { coldStorageId: target.coldStorageId }
            : undefined,
        });
        return;
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-metrics font-metrics text-primary-navy-800">
            Monitoring
          </span>
          <span className="text-body font-body text-subtext-color">
            Monitoring your environment and procedures in real time.
          </span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs>
        <Tabs.Item
          active={tab === "alerts"}
          icon={<FeatherBell />}
          onClick={() => setTab("alerts")}
        >
          Alerts
          <AlertCountBadge count={activeAlertCount} />
        </Tabs.Item>
        <Tabs.Item
          active={tab === "history"}
          icon={<FeatherHistory />}
          onClick={() => setTab("history")}
        >
          History
        </Tabs.Item>
      </Tabs>

      <div className="flex w-full flex-col items-start gap-4 pb-8">
        {/* History sub-view: raw readings, or the episodes closed in this period */}
        {tab === "history" && (
          <HistoryViewToggle value={historyView} onChange={setHistoryView} />
        )}

        {/* Filters: monitoring type + location */}
        <div className="flex w-full flex-wrap items-center gap-3">
          <Select
            placeholder="All monitoring types"
            value={selectedCheckTypeValue}
            onValueChange={handleCheckTypeChange}
          >
            <Select.Item value="ALL">All monitoring types</Select.Item>
            {monitoringTypeOptions.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select>
          <Select
            placeholder="All locations"
            value={locationId ?? "ALL"}
            onValueChange={(val) => {
              setLocationId(val === "ALL" ? undefined : val);
              setPage(0);
            }}
          >
            <Select.Item value="ALL">All locations</Select.Item>
            {locations.map((location) => (
              <Select.Item key={location.id} value={location.id}>
                {location.name}
              </Select.Item>
            ))}
          </Select>
          <Button
            className="ml-auto"
            variant="neutral-secondary"
            icon={<FeatherDownload />}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>

        {/* Table */}
        {isLoading || isPlaceholder ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex w-full items-center justify-center py-16">
            <span className="text-body font-body text-subtext-color">
              {EMPTY_STATE[view]}
            </span>
          </div>
        ) : (
          <>
            <TableScrollContainer>
              <MonitoringTable
                entries={entries}
                view={view}
                activeColumn={effectiveSort.column}
                activeDir={effectiveSort.dir}
                onSort={handleSort}
                onView={handleView}
                canView={canView}
                // Nothing on the resolved list can be acknowledged — it is
                // already closed — so the affordance doesn't reach it.
                onAcknowledge={
                  view === "resolved-alerts" ? undefined : handleAcknowledge
                }
                acknowledgingAlertId={acknowledgingAlertId}
              />
            </TableScrollContainer>

            <Pagination
              page={page + 1}
              pageSize={pageSize}
              totalCount={total}
              onPageChange={(next) => setPage(next - 1)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              itemNoun={ITEM_NOUN[view]}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default MonitoringPage;
